
import React, { useState } from 'react';
import { CalendarEvent } from '@/hooks/calendar/types';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Check, X, Clock, Info, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface SessionRequestItemProps {
  event: CalendarEvent;
  onApprove: (eventId: string) => Promise<void>;
  onReject: (eventId: string) => Promise<void>;
}

const SessionRequestItem: React.FC<SessionRequestItemProps> = ({
  event,
  onApprove,
  onReject
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const formattedDate = format(event.date, 'EEEE d MMMM', { locale: it });
  const formattedTime = `${event.hour}:${event.minute?.toString().padStart(2, '0') || '00'}`;
  
  const getInitials = (name?: string) => {
    if (!name) return "?";
    const parts = name.split(' ');
    if (parts.length > 1) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return parts[0][0].toUpperCase();
  };
  
  const handleApprove = async () => {
    setIsProcessing(true);
    try {
      await onApprove(event.id);
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleReject = async () => {
    setIsProcessing(true);
    try {
      await onReject(event.id);
    } finally {
      setIsProcessing(false);
    }
  };
  
  return (
    <div className="border rounded-md p-4 mb-3 bg-white shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full inline-flex items-center">
              <Clock className="h-3 w-3 mr-1" />
              In attesa
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1.5">
            {event.clientName && (
              <Avatar className="h-8 w-8 border border-gray-200">
                <AvatarImage src="" alt={event.clientName} />
                <AvatarFallback className="text-xs bg-primary/10 text-primary">
                  {getInitials(event.clientName)}
                </AvatarFallback>
              </Avatar>
            )}
            <div>
              <h3 className="text-lg font-medium">{event.title}</h3>
              <p className="text-sm text-gray-600 font-medium">
                {event.clientName || "Cliente"}
              </p>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {formattedDate} - {formattedTime}
          </p>
        </div>
        
        <div className="flex flex-col gap-2">
          {event.clientId && (
            <Link 
              to={`/admin/clients/${event.clientId}`}
              className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
            >
              <Info className="h-4 w-4" />
            </Link>
          )}
        </div>
      </div>
      
      <div className="flex gap-2 mt-3 justify-end">
        <Button
          size="sm"
          variant="outline"
          className="text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600"
          onClick={handleReject}
          disabled={isProcessing}
        >
          <X className="h-4 w-4 mr-1" />
          Rifiuta
        </Button>
        <Button
          size="sm"
          variant="outline" 
          className="text-green-500 border-green-200 hover:bg-green-50 hover:text-green-600"
          onClick={handleApprove}
          disabled={isProcessing}
        >
          <Check className="h-4 w-4 mr-1" />
          Approva
        </Button>
      </div>
    </div>
  );
};

export default SessionRequestItem;
